/**
 * Tiptap rich-text editor for email HTML templates.
 * Emits sanitized raw HTML (not JSON) compatible with Jinja2 email rendering.
 *
 * Dynamic fields: type `#` (or use the toolbar hint) for a searchable variable
 * list. Selection still inserts Jinja `{{ name }}` for the mailer.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { DOMSerializer } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Quote, Minus, Link2, Unlink,
  Undo2, Redo2, Code,
  Highlighter, Superscript as SupIcon, Subscript as SubIcon,
  RectangleHorizontal, Braces, GitBranch,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  EMAIL_CTA_BUTTON_STYLE,
  sanitizeEmailHtml,
} from '@/lib/sanitizeEmailHtml';
import { EMAIL_HTML_SURFACE_CLASS } from '@/lib/emailHtmlSurface';
import {
  buildListLogicBlock,
  defaultItemVariableForList,
} from '@/lib/emailTemplateLogic';
import {
  fromEditorHtml,
  isListVariable,
  toEditorHtml,
} from '@/lib/emailTemplateJinjaDom';
import { labelForVariable } from '@/lib/emailTemplateVars';
import {
  filterVariableOptions,
  matchHashVariableTrigger,
} from '@/lib/emailVariableSuggestion';
import EmailLogicBuilderDialog from '@/components/email/EmailLogicBuilderDialog';
const TEXT_COLORS = [
  { label: 'Default', value: '' },
  { label: 'Black', value: '#111827' },
  { label: 'Gray', value: '#6B7280' },
  { label: 'Blue', value: '#0F62FE' },
  { label: 'Green', value: '#059669' },
  { label: 'Red', value: '#DC2626' },
  { label: 'Amber', value: '#D97706' },
];

const JINJA_HREF_RE = /^\s*\{\{[\s\S]*\}\}\s*$|^\s*\{%[\s\S]*%\}\s*$/;

/** `true` when the href is a pure Jinja token, e.g. `{{ portal_url }}`. */
export function isJinjaHref(href) {
  return JINJA_HREF_RE.test(String(href || ''));
}

const URL_NAME_SEGMENTS = new Set(['url', 'uri', 'link', 'href']);

/** `true` for variables that hold a URL, e.g. `portal_url`, `candidate_url`. */
export function isUrlVariable(name) {
  return String(name || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .some((segment) => URL_NAME_SEGMENTS.has(segment));
}
const EmailLink = Link.extend({
  inclusive: false,
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute('style'),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
  },
}).configure({
  openOnClick: false,
  autolink: false,
  linkOnPaste: true,
  HTMLAttributes: {
    rel: 'noopener noreferrer',
  },
  // `isAllowedUri` — not the deprecated `validate` — is the gate Tiptap applies on
  // parse, render, and setLink. Jinja hrefs must pass it or the anchor is dropped.
  isAllowedUri: (href, ctx) => isJinjaHref(href) || ctx.defaultValidate(href),
});

function escapeHtmlText(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** HTML for the current selection (empty string when caret-only). */
export function getSelectedHtml(editor) {
  if (!editor) return '';
  const { from, to } = editor.state.selection;
  if (from === to) return '';
  const slice = editor.state.doc.slice(from, to);
  const serializer = DOMSerializer.fromSchema(editor.schema);
  const div = document.createElement('div');
  div.appendChild(serializer.serializeFragment(slice.content));
  return div.innerHTML;
}

/**
 * Toggle bullet/ordered list on the current selection.
 * TipTap wraps each selected paragraph as its own `<li>` — so three Enter-separated
 * lines become three bullets. (Markers are restored via editor CSS; Preflight
 * otherwise hides them and the toolbar looks broken.)
 */
export function toggleListCommand(editor, kind = 'bullet') {
  if (!editor) return false;
  const chain = editor.chain().focus();
  return kind === 'ordered'
    ? chain.toggleOrderedList().run()
    : chain.toggleBulletList().run();
}

/** Extension set shared by the editor and its round-trip tests. */
export function createEmailEditorExtensions(placeholder = '') {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      codeBlock: false,
      // Keep hard breaks as <br> for email sign-offs.
    }),
    Underline,
    TextStyle,
    Color,
    Highlight.configure({ multicolor: false }),
    Subscript,
    Superscript,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
      alignments: ['left', 'center', 'right', 'justify'],
    }),
    EmailLink,
    Placeholder.configure({ placeholder }),
  ];
}

function ToolbarButton({ active, disabled, onClick, title, children, testId }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      data-testid={testId}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-foreground disabled:opacity-40',
        active && 'bg-primary/10 text-primary',
      )}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="mx-0.5 h-5 w-px shrink-0 bg-border" aria-hidden />;
}

/**
 * @param {{
 *   value: string,
 *   onChange: (html: string) => void,
 *   variables?: string[],
 *   placeholder?: string,
 *   className?: string,
 *   'data-testid'?: string,
 * }} props
 */
export default function EmailHtmlEditor({
  value,
  onChange,
  variables = [],
  placeholder = 'Type # to insert a dynamic variable…',
  className,
  'data-testid': testId = 'email-html-editor',
}) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  const [logicOpen, setLogicOpen] = useState(false);
  const [logicSelectionHtml, setLogicSelectionHtml] = useState('');
  /** @type {[{ query: string, from: number, to: number, left: number, top: number } | null, Function]} */
  const [suggestion, setSuggestion] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const extensions = useMemo(() => createEmailEditorExtensions(placeholder), [placeholder]);
  const variableOptions = useMemo(
    () => [...new Set(variables.filter(Boolean))].sort(),
    [variables],
  );

  const suggestionRef = useRef(null);
  const activeIndexRef = useRef(0);
  const filteredRef = useRef([]);
  const insertVariableRef = useRef(() => {});
  const variableOptionsRef = useRef(variableOptions);
  variableOptionsRef.current = variableOptions;

  const filteredVariables = useMemo(
    () => (suggestion ? filterVariableOptions(variableOptions, suggestion.query) : []),
    [suggestion, variableOptions],
  );

  suggestionRef.current = suggestion;
  activeIndexRef.current = activeIndex;
  filteredRef.current = filteredVariables;

  const emitChange = useCallback((ed) => {
    const raw = ed.getHTML();
    // Repair TipTap-mangled `{% for %}` lists, then sanitize for persistence.
    onChange(sanitizeEmailHtml(fromEditorHtml(raw)));
  }, [onChange]);

  const syncSuggestion = useCallback((ed) => {
    if (!ed || !variableOptionsRef.current.length) {
      setSuggestion(null);
      return;
    }
    const match = matchHashVariableTrigger(ed);
    if (!match) {
      setSuggestion(null);
      return;
    }
    const coords = ed.view.coordsAtPos(match.from);
    setSuggestion({
      query: match.query,
      from: match.from,
      to: match.to,
      left: coords.left,
      top: coords.bottom + 4,
    });
  }, []);

  const editor = useEditor({
    extensions,
    // Rewrite canonical <ol>{% for %}… into TipTap-safe paragraphs on load.
    content: toEditorHtml(value || ''),
    editorProps: {
      attributes: {
        class: cn(
          'max-w-none min-h-[220px] px-4 py-3 focus:outline-none',
          EMAIL_HTML_SURFACE_CLASS,
        ),
        'data-testid': `${testId}-content`,
      },
      handleKeyDown: (_view, event) => {
        const s = suggestionRef.current;
        if (!s) return false;

        if (event.key === 'Escape') {
          event.preventDefault();
          setSuggestion(null);
          return true;
        }

        const items = filteredRef.current;
        if (!items.length) return false;

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          setActiveIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault();
          setActiveIndex((i) => (i - 1 + items.length) % items.length);
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          event.preventDefault();
          const name = items[activeIndexRef.current];
          if (name) insertVariableRef.current(name, s);
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      emitChange(ed);
      syncSuggestion(ed);
    },
    onSelectionUpdate: ({ editor: ed }) => syncSuggestion(ed),
  });

  // Parent remounts via `key` on template switch — do not re-setContent on every
  // draft keystroke (would reset caret / fight controlled updates).

  useEffect(() => {
    setActiveIndex(0);
  }, [suggestion?.query, suggestion?.from]);

  useEffect(() => {
    if (!suggestion || !filteredVariables.length) return;
    if (activeIndex >= filteredVariables.length) {
      setActiveIndex(0);
    }
  }, [suggestion, filteredVariables, activeIndex]);

  const applyLink = () => {
    if (!editor) return;
    const href = linkHref.trim();
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setLinkOpen(false);
      return;
    }
    const chain = editor.chain().focus();
    if (editor.state.selection.empty) {
      // Nothing selected: insert the anchor, defaulting its text to the href so a
      // bare `{{ portal_url }}` still becomes a clickable link.
      const label = linkLabel.trim() || href;
      chain.insertContent(
        `<a href="${href.replace(/"/g, '&quot;')}">${escapeHtmlText(label)}</a>`,
      ).run();
    } else {
      chain.extendMarkRange('link').setLink({ href }).run();
    }
    setLinkOpen(false);
    setLinkHref('');
    setLinkLabel('');
  };

  const insertButton = () => {
    if (!editor) return;
    const href = window.prompt('Button URL (may use {{ portal_url }})', '{{ portal_url }}');
    if (href == null) return;
    const label = window.prompt('Button label', 'Open HireIQ') || 'Open HireIQ';
    const safeHref = href.replace(/"/g, '&quot;');
    editor
      .chain()
      .focus()
      .insertContent(
        `<p><a href="${safeHref}" style="${EMAIL_CTA_BUTTON_STYLE}">${label}</a></p>`,
      )
      .run();
  };

  const insertVariableAt = useCallback((name, range) => {
    if (!editor || !name) return;
    setSuggestion(null);
    const chain = editor.chain().focus();
    if (range && typeof range.from === 'number' && typeof range.to === 'number' && range.to > range.from) {
      chain.deleteRange({ from: range.from, to: range.to });
    }

    // List vars dump as Python-style arrays if inserted as {{ questions }};
    // insert a proper numbered loop instead (matches briefing preview).
    if (isListVariable(name)) {
      const html = buildListLogicBlock({
        listVariable: name,
        itemVariable: defaultItemVariableForList(name),
        format: 'ol',
        emptyHtml: '<em>No items included.</em>',
        wrapInIf: true,
      });
      chain.insertContent(toEditorHtml(html)).run();
      return;
    }
    const token = `{{ ${name} }}`;
    if (isUrlVariable(name) && !editor.isActive('link')) {
      // URL variables read as links in the sent email, so insert them as anchors
      // instead of bare text.
      chain.insertContent(`<a href="${token}">${token}</a>`).run();
      return;
    }
    chain.insertContent(token).run();
  }, [editor]);

  insertVariableRef.current = insertVariableAt;

  const startVariableTrigger = () => {
    if (!editor) return;
    editor.chain().focus().insertContent('#').run();
  };

  const openLogicDialog = () => {
    if (!editor) return;
    setLogicSelectionHtml(getSelectedHtml(editor));
    setLogicOpen(true);
  };

  const insertLogicHtml = (html) => {
    if (!editor || !html) return;
    const { from, to } = editor.state.selection;
    const chain = editor.chain().focus();
    if (from !== to) {
      chain.insertContentAt({ from, to }, html).run();
    } else {
      chain.insertContent(html).run();
    }
  };

  if (!editor) {
    return (
      <div
        className={cn('rounded-lg border bg-muted/30 px-4 py-8 text-sm text-muted-foreground', className)}
        data-testid={testId}
      >
        Loading editor…
      </div>
    );
  }

  return (
    <div className={cn('overflow-hidden rounded-lg border bg-white', className)} data-testid={testId}>
      <div
        className="flex flex-wrap items-center gap-0.5 border-b bg-secondary/30 px-2 py-1.5"
        data-testid={`${testId}-toolbar`}
      >
        <ToolbarButton
          title="Undo" testId={`${testId}-undo`}
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Redo" testId={`${testId}-redo`}
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarDivider />

        <ToolbarButton
          title="Bold" testId={`${testId}-bold`}
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Italic" testId={`${testId}-italic`}
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Underline" testId={`${testId}-underline`}
          active={editor.isActive('underline')}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Strikethrough" testId={`${testId}-strike`}
          active={editor.isActive('strike')}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Inline code" testId={`${testId}-code`}
          active={editor.isActive('code')}
          onClick={() => editor.chain().focus().toggleCode().run()}
        >
          <Code className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Superscript" testId={`${testId}-sup`}
          active={editor.isActive('superscript')}
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
        >
          <SupIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Subscript" testId={`${testId}-sub`}
          active={editor.isActive('subscript')}
          onClick={() => editor.chain().focus().toggleSubscript().run()}
        >
          <SubIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarDivider />

        <ToolbarButton
          title="Align left" testId={`${testId}-align-left`}
          active={editor.isActive({ textAlign: 'left' })}
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <AlignLeft className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Align center" testId={`${testId}-align-center`}
          active={editor.isActive({ textAlign: 'center' })}
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <AlignCenter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Align right" testId={`${testId}-align-right`}
          active={editor.isActive({ textAlign: 'right' })}
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <AlignRight className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Justify" testId={`${testId}-align-justify`}
          active={editor.isActive({ textAlign: 'justify' })}
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
        >
          <AlignJustify className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarDivider />

        <ToolbarButton
          title="Bullet list" testId={`${testId}-bullet-list`}
          active={editor.isActive('bulletList')}
          onClick={() => toggleListCommand(editor, 'bullet')}
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Numbered list" testId={`${testId}-ordered-list`}
          active={editor.isActive('orderedList')}
          onClick={() => toggleListCommand(editor, 'ordered')}
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Blockquote" testId={`${testId}-blockquote`}
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          <Quote className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton
          title="Horizontal rule" testId={`${testId}-hr`}
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
        >
          <Minus className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarDivider />

        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Insert link"
              data-testid={`${testId}-link`}
              onMouseDown={(e) => e.preventDefault()}
              className={cn(
                'inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground',
                'hover:bg-accent hover:text-foreground',
                editor.isActive('link') && 'bg-primary/10 text-primary',
              )}
            >
              <Link2 className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 space-y-2 p-3" align="start">
            <p className="text-xs font-medium text-foreground">Hyperlink</p>
            <Input
              placeholder="https://… or {{ portal_url }}"
              value={linkHref}
              onChange={(e) => setLinkHref(e.target.value)}
              className="font-mono text-xs"
              data-testid={`${testId}-link-href`}
            />
            {editor.state.selection.empty && (
              <Input
                placeholder="Link text (optional)"
                value={linkLabel}
                onChange={(e) => setLinkLabel(e.target.value)}
                className="text-xs"
                data-testid={`${testId}-link-label`}
              />
            )}
            <div className="flex gap-2">
              <Button size="sm" className="flex-1" onClick={applyLink} data-testid={`${testId}-link-apply`}>
                Apply
              </Button>
              <Button
                size="sm" variant="outline" className="flex-1"
                onClick={() => {
                  editor.chain().focus().extendMarkRange('link').unsetLink().run();
                  setLinkOpen(false);
                }}
                data-testid={`${testId}-link-remove`}
              >
                <Unlink className="mr-1 h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        <ToolbarButton
          title="CTA button" testId={`${testId}-button`}
          onClick={insertButton}
        >
          <RectangleHorizontal className="h-3.5 w-3.5" />
        </ToolbarButton>

        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              title="Text color"
              data-testid={`${testId}-color`}
              onMouseDown={(e) => e.preventDefault()}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <span className="text-[11px] font-bold leading-none">A</span>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-40 p-2" align="start">
            <div className="grid grid-cols-4 gap-1">
              {TEXT_COLORS.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  title={c.label}
                  className="h-7 w-7 rounded border"
                  style={{ background: c.value || '#fff' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (!c.value) editor.chain().focus().unsetColor().run();
                    else editor.chain().focus().setColor(c.value).run();
                  }}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <ToolbarButton
          title="Highlight" testId={`${testId}-highlight`}
          active={editor.isActive('highlight')}
          onClick={() => editor.chain().focus().toggleHighlight().run()}
        >
          <Highlighter className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarDivider />

        <div className="ml-auto flex flex-wrap items-center gap-1.5 pl-1">
          <button
            type="button"
            title="Build dynamic content (show/hide or list items)"
            data-testid={`${testId}-insert-logic`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={openLogicDialog}
            className={cn(
              'inline-flex h-8 items-center gap-1 rounded-md px-2 text-xs text-muted-foreground',
              'hover:bg-accent hover:text-foreground',
              logicOpen && 'bg-primary/10 text-primary',
            )}
          >
            <GitBranch className="h-3.5 w-3.5" />
            Insert logic
          </button>

          <EmailLogicBuilderDialog
            open={logicOpen}
            onOpenChange={setLogicOpen}
            variables={variableOptions}
            selectedHtml={logicSelectionHtml}
            onInsert={insertLogicHtml}
            testId={testId}
          />

          {variableOptions.length > 0 && (
            <button
              type="button"
              title="Type # to insert a dynamic variable"
              data-testid={`${testId}-variable-hint`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={startVariableTrigger}
              className={cn(
                'inline-flex h-8 items-center gap-1 rounded-md border border-dashed border-border',
                'bg-background px-2 text-xs text-muted-foreground',
                'hover:bg-accent hover:text-foreground',
                suggestion && 'border-primary/40 bg-primary/5 text-primary',
              )}
            >
              <Braces className="h-3.5 w-3.5" />
              Type # for variables
            </button>
          )}
        </div>
      </div>

      <div className="relative">
        <EditorContent editor={editor} />
      </div>

      {suggestion && typeof document !== 'undefined' && createPortal(
        <div
          role="listbox"
          aria-label="Dynamic variables"
          data-testid={`${testId}-variable-suggestion`}
          className="fixed z-[200] max-h-56 w-72 overflow-auto rounded-md border bg-white p-1 shadow-lg"
          style={{ top: suggestion.top, left: suggestion.left }}
        >
          {filteredVariables.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No variables match “{suggestion.query}”
            </p>
          ) : (
            filteredVariables.map((name, index) => (
              <button
                key={name}
                type="button"
                role="option"
                aria-selected={index === activeIndex}
                data-testid={`${testId}-variable-option-${name}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertVariableAt(name, suggestion);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-xs',
                  index === activeIndex ? 'bg-primary/10 text-foreground' : 'hover:bg-accent',
                )}
              >
                <span className="font-medium text-foreground">{labelForVariable(name)}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{`{{ ${name} }}`}</span>
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
